/**
 * ============================================================================
 * AUTH SLICE - Complete Authentication State Management
 * ============================================================================
 *
 * This slice manages ALL authentication flows with proper token handling
 * via tokenManager at reducer level.
 *
 * Flows Handled:
 * 1. Login → accessToken, refreshToken, optional publicToken (changePasswordToken)
 * 2. OTP Request → publicToken
 * 3. OTP Verify → publicToken
 * 4. Password Reset → no tokens
 * 5. Agreement Sign → optional publicToken
 * 6. First Login Password Change → accessToken, refreshToken
 * 7. Token Refresh → accessToken, refreshToken
 * 8. Logout → clear all tokens
 *
 * Token Storage: ONLY via tokenManager in reducer fulfilled cases
 * Actions: Extract tokens from response, return to reducer
 *
 * Following: Classveew organization.slice.ts pattern
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import i18n from '../../i18n/i18n';
import { tokenManager } from '../../utility/tokenManager';
import { APP_STATUS } from '../../constants';
import { TOKEN_TYPES, USER_ROLES } from '../../constant/auth.constant';
import {
  loginUser,
  selectOrganization,
  selectOrganizationByStudent,
  fetchGuardianStudents,
  requestOtpEmail,
  requestUserVerification,
  verifyOtp,
  resetPassword,
  submitAgreementSign,
  changePasswordFirstLogin,
  logOut,
  getRefreshToken,
} from '../action/auth.action';
import type {
  IRequestOtpEmailResponseBody,
  IVerifyOtpResponseBody,
  IResetPasswordResponseBody,
  ISubmitAgreementSignResponse,
  ILoginResponse,
  IAuthOrganizationOption,
  IGuardianStudent,
} from '../../interface/auth.interface';
import type { RootState } from '../store';

/**
 * Auth State Interface
 */
interface IAuthState {
  // User data
  user: {
    email: string;
    first_name: string;
    last_name: string;
  } | null;
  organization: string | null;
  role: string | null;
  userId: string | null;

  // Multi-organization login flow (ORGANIZATION_SELECTION_REQUIRED)
  organizationOptions: IAuthOrganizationOption[] | null;

  // Guardian (Parent) state — children of the current org-scoped session and
  // the currently selected student (drives the sidebar org + student selector)
  children: IGuardianStudent[] | null;
  selectedStudent: IGuardianStudent | null;
  // ALL the guardian's students as a flat list, each carrying its organization
  // (GET /me/students) — the sidebar selector groups these by organization
  guardianStudents: IGuardianStudent[] | null;

  // Status flags
  isAuthenticated: boolean;
  status: APP_STATUS;
  error: string | null;

  // Response data for multi-step flows
  otpResponse: IRequestOtpEmailResponseBody | null;
  verifyOtpResponse: IVerifyOtpResponseBody | null;
  resetPasswordResponse: IResetPasswordResponseBody | null;
  agreementSignResponse: ISubmitAgreementSignResponse | null;
}

/**
 * Initial State
 */
const initialState: IAuthState = {
  user: null,
  organization: null,
  role: null,
  userId: null,
  organizationOptions: null,
  children: null,
  selectedStudent: null,
  guardianStudents: null,
  isAuthenticated: false,
  status: APP_STATUS.IDLE,
  error: null,
  otpResponse: null,
  verifyOtpResponse: null,
  resetPasswordResponse: null,
  agreementSignResponse: null,
};

/**
 * Apply a full (complete-login shaped) auth response body to state.
 * Shared by login success, selectOrganization and selectOrganizationByStudent.
 *
 * For guardians (role 'Parent') this also stores `profile.children` and
 * auto-selects the first ACTIVE child (mirrors the mobile app behavior).
 */
const applyFullAuthResponse = (state: IAuthState, response: ILoginResponse | undefined) => {
  const profile = response?.profile;
  state.user = profile
    ? {
        email: profile.email,
        first_name: profile.first_name,
        last_name: profile.last_name,
      }
    : null;

  state.organization = response?.organizationName || profile?.organization || null;
  state.role = response?.role || null;
  state.userId = profile?.userId || null;

  if (response?.role === USER_ROLES.PARENT) {
    const children = profile?.children || [];
    state.children = children;
    state.selectedStudent =
      children.find((child) => child.status === true) || children[0] || null;
  } else {
    state.children = null;
    state.selectedStudent = null;
    state.guardianStudents = null;
  }
};

/**
 * Auth Slice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Reset auth state (for logout or manual reset)
     */
    resetAuthState: () => initialState,

    /**
     * Set user data manually (if needed)
     */
    setUser: (
      state,
      action: PayloadAction<{
        email: string;
        first_name: string;
        last_name: string;
      }>,
    ) => {
      state.user = action.payload;
    },

    /**
     * Clear error manually
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Guardian picks a student belonging to the CURRENT organization —
     * local selection only, no token exchange (mirrors mobile).
     */
    setSelectedStudent: (state, action: PayloadAction<IGuardianStudent>) => {
      state.selectedStudent = action.payload;
    },
  },
  extraReducers: (builder) => {
    /**
     * ========================================================================
     * LOGIN FLOW
     * ========================================================================
     */
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;

        // Extract response and tokens
        const { response, accessToken, refreshToken, publicToken, tempToken, organizations } =
          action.payload;

        // Store user data (response has profile at root level).
        // For guardians this also stores children + auto-selects the first
        // active child (mirrors mobile).
        applyFullAuthResponse(state, response);

        // Store tokens via tokenManager (PRIMARY TOKEN STORAGE)
        if (accessToken && refreshToken) {
          tokenManager.setTokens({
            accessToken,
            refreshToken,
          });
          state.isAuthenticated = true;
        }

        // First login scenario: only publicToken (changePasswordToken) present
        if (publicToken && !accessToken) {
          tokenManager.setToken('publicToken', publicToken);
          state.isAuthenticated = false; // Not fully authenticated until password changed
        }

        // Multi-organization scenario: short-lived temp token + organization
        // list; the user must pick an organization before real tokens exist.
        if (tempToken && !accessToken) {
          tokenManager.setToken(TOKEN_TYPES.TEMP_TOKEN, tempToken);
          state.isAuthenticated = false;
        }
        state.organizationOptions = organizations || null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || i18n.t('toast.login_failed', 'Login failed');
        state.isAuthenticated = false;
      });

    /**
     * ========================================================================
     * ORGANIZATION SELECTION FLOW (multi-org login — temp token exchange)
     * ========================================================================
     */
    builder
      .addCase(selectOrganization.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(selectOrganization.fulfilled, (state, action) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;

        const { response, accessToken, refreshToken } = action.payload;

        // Same shape as a complete login — apply user/org/role/children state
        applyFullAuthResponse(state, response);

        if (accessToken && refreshToken) {
          tokenManager.setTokens({ accessToken, refreshToken });
          state.isAuthenticated = true;
        }

        // Temp token is single-use — clear it along with the pending org list
        tokenManager.clearToken(TOKEN_TYPES.TEMP_TOKEN);
        state.organizationOptions = null;
      })
      .addCase(selectOrganization.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || 'Organization selection failed';
      });

    /**
     * ========================================================================
     * ORGANIZATION SELECTION BY STUDENT FLOW (guardian student switch)
     * ========================================================================
     */
    builder
      .addCase(selectOrganizationByStudent.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(selectOrganizationByStudent.fulfilled, (state, action) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;

        const { response, accessToken, refreshToken } = action.payload;
        const guardianStudents = state.guardianStudents;

        // Full auth response scoped to the picked student's organization
        applyFullAuthResponse(state, response);
        // applyFullAuthResponse resets guardianStudents for non-parent roles
        // only; keep the fetched flat list for the sidebar selector.
        state.guardianStudents = guardianStudents;

        // Select the student the guardian actually picked (NOT the default
        // first-active-child selection applied above).
        const { studentId } = action.meta.arg;
        const picked =
          guardianStudents?.find((student) => student.id === studentId) ||
          state.children?.find((child) => child.id === studentId);
        if (picked) {
          state.selectedStudent = picked;
        }

        // NEW org-scoped tokens replace the current ones
        if (accessToken && refreshToken) {
          tokenManager.setTokens({ accessToken, refreshToken });
          state.isAuthenticated = true;
        }
      })
      .addCase(selectOrganizationByStudent.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || 'Student switch failed';
      });

    /**
     * ========================================================================
     * GUARDIAN STUDENTS FLOW (sidebar organization + student selector data)
     * ========================================================================
     */
    builder
      .addCase(fetchGuardianStudents.fulfilled, (state, action) => {
        state.guardianStudents = action.payload;
      })
      .addCase(fetchGuardianStudents.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to load students';
      });

    /**
     * ========================================================================
     * OTP REQUEST FLOW (Email/SMS verification)
     * ========================================================================
     */
    builder
      .addCase(requestOtpEmail.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(requestOtpEmail.fulfilled, (state, action) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;

        // Store response and publicToken
        const { response, publicToken } = action.payload;
        state.otpResponse = response;

        // Store publicToken for OTP verification step
        tokenManager.setToken('publicToken', publicToken);
      })
      .addCase(requestOtpEmail.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || i18n.t('toast.otp_request_failed', 'OTP request failed');
      });

    /**
     * ========================================================================
     * USER VERIFICATION REQUEST FLOW
     * ========================================================================
     */
    builder
      .addCase(requestUserVerification.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(requestUserVerification.fulfilled, (state) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;
      })
      .addCase(requestUserVerification.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || i18n.t('toast.verification_request_failed', 'Verification request failed');
      });

    /**
     * ========================================================================
     * OTP VERIFY FLOW
     * ========================================================================
     */
    builder
      .addCase(verifyOtp.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;

        // Store response and publicToken for password reset
        const { response, publicToken } = action.payload;
        state.verifyOtpResponse = response;

        // Store publicToken for password reset step
        tokenManager.setToken('publicToken', publicToken);
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || i18n.t('toast.otp_verification_failed', 'OTP verification failed');
      });

    /**
     * ========================================================================
     * PASSWORD RESET FLOW
     * ========================================================================
     */
    builder
      .addCase(resetPassword.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;

        // Store response
        const { response } = action.payload;
        state.resetPasswordResponse = response;

        // Clear publicToken after successful password reset
        tokenManager.clearToken('publicToken');
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || i18n.t('toast.password_reset_failed', 'Password reset failed');
      });

    /**
     * ========================================================================
     * AGREEMENT SIGN FLOW
     * ========================================================================
     */
    builder
      .addCase(submitAgreementSign.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(submitAgreementSign.fulfilled, (state, action) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;

        // Extract publicToken from response (backend only returns X-Change-Password-Token)
        const { response, publicToken } = action.payload;
        state.agreementSignResponse = response;

        // Store publicToken for first login password change
        // Backend does NOT return accessToken/refreshToken during agreement sign
        if (publicToken) {
          tokenManager.setToken('publicToken', publicToken);
        }

        // User is NOT authenticated after agreement sign
        // Must complete first login password change to get accessToken/refreshToken
        state.isAuthenticated = false;
      })
      .addCase(submitAgreementSign.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || i18n.t('toast.agreement_sign_failed', 'Agreement sign failed');
      });

    /**
     * ========================================================================
     * FIRST LOGIN PASSWORD CHANGE FLOW
     * ========================================================================
     */
    builder
      .addCase(changePasswordFirstLogin.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(changePasswordFirstLogin.fulfilled, (state, action) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;

        // Extract tokens and set authentication
        const { accessToken, refreshToken } = action.payload;

        // Store tokens via tokenManager
        tokenManager.setTokens({
          accessToken,
          refreshToken,
        });

        // Clear publicToken (changePasswordToken) after successful password change
        tokenManager.clearToken('publicToken');

        // User is now fully authenticated
        state.isAuthenticated = true;
      })
      .addCase(changePasswordFirstLogin.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || i18n.t('toast.password_change_failed', 'Password change failed');
      });

    /**
     * ========================================================================
     * LOGOUT FLOW
     * ========================================================================
     */
    builder
      .addCase(logOut.pending, (state) => {
        state.status = APP_STATUS.LOADING;
      })
      .addCase(logOut.fulfilled, (state) => {
        // Clear all tokens via tokenManager
        tokenManager.clearAllTokens();

        // Reset entire auth state
        Object.assign(state, initialState);
      })
      .addCase(logOut.rejected, (state) => {
        // Even if API call fails, clear tokens locally
        tokenManager.clearAllTokens();

        // Reset state
        Object.assign(state, initialState);
      });

    /**
     * ========================================================================
     * TOKEN REFRESH FLOW
     * ========================================================================
     */
    builder
      .addCase(getRefreshToken.pending, (state) => {
        state.status = APP_STATUS.LOADING;
        state.error = null;
      })
      .addCase(getRefreshToken.fulfilled, (state, action) => {
        state.status = APP_STATUS.SUCCESS;
        state.error = null;

        // Update tokens via tokenManager
        const { accessToken, refreshToken } = action.payload;
        tokenManager.setTokens({
          accessToken,
          refreshToken,
        });

        // Maintain authenticated state
        state.isAuthenticated = true;
      })
      .addCase(getRefreshToken.rejected, (state, action) => {
        state.status = APP_STATUS.ERROR;
        state.error = action.payload?.message || i18n.t('toast.token_refresh_failed', 'Token refresh failed');

        // Clear tokens if refresh fails
        tokenManager.clearAllTokens();
        state.isAuthenticated = false;
      });
  },
});

/**
 * Export actions
 */
export const { resetAuthState, setUser, clearError, setSelectedStudent } = authSlice.actions;

/**
 * Selectors
 */
export const selectAuthRole = (state: RootState) => state.auth.role;
export const selectOrganizationOptions = (state: RootState) => state.auth.organizationOptions;
export const selectGuardianChildren = (state: RootState) => state.auth.children;
export const selectSelectedStudent = (state: RootState) => state.auth.selectedStudent;
export const selectGuardianStudents = (state: RootState) => state.auth.guardianStudents;

/**
 * Export reducer
 */
export default authSlice.reducer;
